// src/pages/TrendView.tsx
import React, { useMemo, useState, useEffect } from "react";
import Papa from "papaparse";
import { useData } from "../context/DataContext";

// NEW Brand palette
const BRAND = {
  bg: "#EEF5FF",
  panel: "#B4D4FF",
  border: "#86B6F6",
  accent: "#176B87",
  ink: "#0B2230",
  textOnAccent: "#FFFFFF",
};

type Match = {
  date?: string;
  home_team?: string;
  away_team?: string;
  home_score?: string | number;
  away_score?: string | number;
  competition?: string;
  stadium?: string;
  city?: string;
  country?: string;
  neutral?: string | boolean;
  world_cup?: string | boolean;
  [key: string]: any;
};

const COLUMNS: Array<keyof Match> = [
  "date",
  "home_team",
  "away_team",
  "home_score",
  "away_score",
  "competition",
  "stadium",
  "city",
  "country",
  "neutral",
  "world_cup",
];

const TrendView: React.FC = () => {
  const { rows: rawRows, loading } = useData();
  const rows = useMemo<Match[]>(
    () => rawRows.filter((r) => r.date) as Match[],
    [rawRows]
  );

  // UX state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [query, setQuery] = useState("");
  const [pageInput, setPageInput] = useState("");
  const [sort, setSort] = useState<{ key: keyof Match; asc: boolean }>({ key: "date", asc: true });

  // Filter
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => COLUMNS.some((c) => String(r?.[c] ?? "").toLowerCase().includes(q)));
  }, [rows, query]);

  // Sort
  const sorted = useMemo(() => {
    const { key, asc } = sort;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = (a?.[key] ?? "") as string;
      const vb = (b?.[key] ?? "") as string;
      // numeric sort for scores; date-friendly lexicographic (ISO) for date
      if (key === "home_score" || key === "away_score") {
        const na = Number(va);
        const nb = Number(vb);
        return asc ? na - nb : nb - na;
      }
      return asc ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return arr;
  }, [filtered, sort]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, currentPage, pageSize]);

  const goToPage = () => {
    const pageNumber = Number(pageInput);
    if (!Number.isInteger(pageNumber)) return;

    if (pageNumber < 1) setPage(1);
    else if (pageNumber > totalPages) setPage(totalPages);
    else setPage(pageNumber);
  };

  const onPageKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") goToPage();
  };

  // Reset page on controls change
  useEffect(() => {
    setPage(1);
  }, [query, pageSize, sort]);

  // CSV download (raw data)
  const downloadCsv = () => {
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "international-rugby-results.csv";
    a.click();
  };

  const headerClick = (key: keyof Match) => {
    setSort((prev) => (prev.key === key ? { key, asc: !prev.asc } : { key, asc: true }));
  };

  const sortArrow = (key: keyof Match) => (sort.key === key ? (sort.asc ? " ▲" : " ▼") : "");

  const renderBoolPill = (val: any) => {
    const s = String(val).toLowerCase();
    if (s === "true" || s === "1") return <span className="pill pill-yes">True</span>;
    if (s === "false" || s === "0") return <span className="pill pill-no">False</span>;
    return <span className="pill pill-na">—</span>;
  };

  return (
    <div className="tv-root">
      <style>{`
        :root {
          --bg: ${BRAND.bg};
          --surface: #ffffff;
          --border: ${BRAND.border};
          --ink: ${BRAND.ink};
          --muted: rgba(11,34,48,0.72);
          --brand: ${BRAND.accent};
          --onBrand: ${BRAND.textOnAccent};
          --shadow-md: 0 14px 30px rgba(23,107,135,0.14), 0 2px 10px rgba(0,0,0,0.04);
          --radius: 14px;

          --yes-bg: rgba(39, 174, 96, 0.10);
          --yes-fg: #1e7f4f;
          --yes-bd: rgba(39, 174, 96, 0.28);

          --no-bg: rgba(231, 76, 60, 0.10);
          --no-fg: #a53a2f;
          --no-bd: rgba(231, 76, 60, 0.28);

          --na-bg: rgba(0,0,0,0.04);
          --na-fg: #66727a;
          --na-bd: rgba(0,0,0,0.10);
        }

        .tv-root {
          min-height: 100vh;
          background: var(--bg);
          color: var(--ink);
          padding: 20px;
        }

        .tv-wrap {
          max-width: 1280px;
          margin: 0 auto;
        }

        .tv-title {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 10px;
          margin-bottom: 14px;
        }
        .tv-title h2 {
          margin: 0;
          font-weight: 900;
          letter-spacing: 0.2px;
          color: var(--ink);
        }
        .tv-title .sub {
          color: var(--muted);
          font-size: 13px;
        }

        .tv-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          box-shadow: var(--shadow-md);
          overflow: hidden;
        }

        /* Toolbar */
        .tv-toolbar {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 10px;
          align-items: center;
          padding: 12px;
          border-bottom: 1px solid rgba(134,182,246,0.7);
          background: linear-gradient(180deg, ${BRAND.panel} 0%, #ffffff 70%);
        }
        @media (max-width: 900px) {
          .tv-toolbar { grid-template-columns: 1fr 1fr; }
          .tv-toolbar .dl { grid-column: 1 / -1; justify-self: stretch; }
        }
        .controls { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

        .input, .select, .btn {
          height: 40px;
          border: 1px solid rgba(134,182,246,0.95);
          background: #fff;
          color: var(--ink);
          border-radius: 10px;
          padding: 0 12px;
          font-size: 14px;
          outline: none;
          transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.08s ease;
        }
        .input:focus, .select:focus {
          border-color: var(--brand);
          box-shadow: 0 0 0 3px rgba(23,107,135,0.18);
        }
        .btn {
          font-weight: 800;
          letter-spacing: 0.2px;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer;
        }
        .btn:hover { transform: translateY(-1px); }
        .btn.primary {
          background: var(--brand);
          color: var(--onBrand);
          border-color: var(--brand);
        }
        .btn.primary:hover { filter: brightness(0.97); }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        /* Table */
        .tv-table-wrap {
          width: 100%;
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        thead th {
          position: sticky; top: 0; z-index: 1;
          background: rgba(180,212,255,0.55);
          color: var(--ink);
          font-weight: 900;
          text-align: left;
          border-bottom: 1px solid rgba(134,182,246,0.8);
          padding: 12px 14px;
          white-space: nowrap;
          cursor: pointer;
        }
        thead th:hover { background: rgba(180,212,255,0.75); }
        tbody td {
          border-bottom: 1px solid rgba(134,182,246,0.35);
          padding: 12px 14px;
          vertical-align: top;
        }
        tbody tr:nth-child(even) td { background: rgba(238,245,255,0.55); }
        tbody tr:hover td { background: rgba(180,212,255,0.35); }

        /* Pills */
        .pill {
          display: inline-flex;
          align-items: center;
          height: 24px;
          padding: 0 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.2px;
          border: 1px solid transparent;
          white-space: nowrap;
        }
        .pill-yes { background: var(--yes-bg); color: var(--yes-fg); border-color: var(--yes-bd); }
        .pill-no  { background: var(--no-bg);  color: var(--no-fg);  border-color: var(--no-bd); }
        .pill-na  { background: var(--na-bg);  color: var(--na-fg);  border-color: var(--na-bd); }

        .meta {
          color: var(--muted);
          font-size: 12px;
          padding: 10px 12px 0;
        }

        .counter {
          background: rgba(238,245,255,0.8);
          color: var(--ink);
          border: 1px solid rgba(134,182,246,0.95);
          height: 40px; border-radius: 10px;
          display: inline-flex; align-items: center; gap: 8px;
          padding: 0 12px; font-weight: 900;
        }
      `}</style>

      <div className="tv-wrap">
        <div className="tv-title">
          <h2>International Rugby — Raw Results</h2>
          <span className="sub">
            Columns:{" "}
            <code>
              date, home_team, away_team, home_score, away_score, competition, stadium, city,
              country, neutral, world_cup
            </code>
          </span>
        </div>

        <div className="tv-card">
          <div className="tv-toolbar">
            <div className="controls">
              <input
                className="input"
                type="text"
                placeholder="Filter (team, city, competition, year, stadium...)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Filter table"
                style={{ minWidth: 260 }}
              />
              <select
                className="select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Rows per page"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <button className="btn" onClick={() => setPage(1)} title="Go to first page">
                First
              </button>
            </div>

            <div className="controls" style={{ justifySelf: "end" }}>
              <input
                className="input"
                type="number"
                min={1}
                max={totalPages}
                placeholder="Page #"
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={onPageKeyDown}
                style={{ width: 90 }}
              />
              <button className="btn" onClick={goToPage}>
                Go
              </button>
              <button
                className="btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                aria-label="Previous page"
              >
                ← Prev
              </button>
              <div className="counter" aria-live="polite">
                Page {currentPage} / {totalPages}
              </div>
              <button
                className="btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                Next →
              </button>
            </div>

            <button className="btn primary dl" onClick={downloadCsv} aria-label="Download CSV">
              Download CSV
            </button>
          </div>

          <div className="meta">{loading ? "Loading…" : `${sorted.length.toLocaleString()} rows`}</div>

          <div className="tv-table-wrap">
            <table role="table" aria-label="Raw results table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={String(c)} onClick={() => headerClick(c)}>
                      {String(c)}
                      {sortArrow(c)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && Array.from({ length: pageSize }).map((_, i) => (
                  <tr key={`skel-${i}`}>
                    {COLUMNS.map((c) => (
                      <td key={String(c)}>
                        <div style={{ height: 14, background: "rgba(134,182,246,0.2)", borderRadius: 4, width: "80%" }} />
                      </td>
                    ))}
                  </tr>
                ))}

                {!loading && pageRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={COLUMNS.length}
                      style={{ textAlign: "center", padding: 24, color: "rgba(11,34,48,0.65)" }}
                    >
                      No data.
                    </td>
                  </tr>
                )}

                {pageRows.map((r, idx) => (
                  <tr key={`${r.date}-${idx}`}>
                    <td>{r.date ?? ""}</td>
                    <td>{r.home_team ?? ""}</td>
                    <td>{r.away_team ?? ""}</td>
                    <td>{r.home_score ?? ""}</td>
                    <td>{r.away_score ?? ""}</td>
                    <td>{r.competition ?? ""}</td>
                    <td>{r.stadium ?? ""}</td>
                    <td>{r.city ?? ""}</td>
                    <td>{r.country ?? ""}</td>
                    <td>{renderBoolPill(r.neutral)}</td>
                    <td>{renderBoolPill(r.world_cup)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrendView;
